import type { UserRole } from '@/types/permissions'

export interface Command {
  id: string
  label: string
  labelKey?: string // i18n key
  icon: string
  path?: string // For navigation commands
  action?: () => void // For action commands
  keywords?: string[] // Additional search terms
  category: 'page' | 'action' | 'setting'
  roles?: UserRole[] // Roles that can access this command (undefined = all roles)
}

export const commands: Command[] = [
  // Pages - Dashboard
  {
    id: 'dashboard',
    label: 'Dashboard',
    labelKey: 'navigation.dashboard',
    icon: 'pi pi-th-large',
    path: '/dashboard',
    keywords: ['home', 'overview', 'main'],
    category: 'page',
  },

  // Pages - Orders (sales unified into orders)
  {
    id: 'orders',
    label: 'Orders',
    labelKey: 'navigation.orders',
    icon: 'pi pi-shopping-bag',
    path: '/orders',
    keywords: ['purchases', 'transactions', 'sales', 'revenue', 'ventes'],
    category: 'page',
  },
  {
    id: 'orders-board',
    label: 'Orders Board',
    labelKey: 'navigation.ordersBoard',
    icon: 'pi pi-th-large',
    path: '/orders/board',
    keywords: ['kanban', 'workflow', 'analytics', 'tableau'],
    category: 'page',
  },

  // Pages - Inventory
  {
    id: 'products',
    label: 'Products',
    labelKey: 'navigation.products',
    icon: 'pi pi-box',
    path: '/products',
    keywords: ['catalog', 'items', 'inventory'],
    category: 'page',
  },
  {
    id: 'product-items',
    label: 'Product Items',
    labelKey: 'navigation.productItems',
    icon: 'pi pi-list',
    path: '/product-items',
    keywords: ['inventory', 'stock', 'sku'],
    category: 'page',
  },
  {
    id: 'arrivals',
    label: 'Arrivals',
    labelKey: 'navigation.arrivals',
    icon: 'pi pi-inbox',
    path: '/arrivals',
    keywords: ['shipments', 'incoming', 'consignment'],
    category: 'page',
    roles: ['admin', 'sales', 'accountant'],
  },

  // Pages - CRM
  {
    id: 'customers',
    label: 'Customers',
    labelKey: 'navigation.customers',
    icon: 'pi pi-users',
    path: '/customers',
    keywords: ['clients', 'contacts', 'people', 'crm'],
    category: 'page',
  },

  // Pages - Logistics
  {
    id: 'deliveries',
    label: 'Deliveries',
    labelKey: 'navigation.deliveries',
    icon: 'pi pi-truck',
    path: '/deliveries',
    keywords: ['shipping', 'dispatch', 'logistics'],
    category: 'page',
  },
  {
    id: 'deliveries-kanban',
    label: 'Deliveries Kanban',
    labelKey: 'navigation.kanban',
    icon: 'pi pi-th-large',
    path: '/deliveries/kanban',
    keywords: ['board', 'visual', 'workflow', 'status'],
    category: 'page',
  },
  {
    id: 'deliveries-assign',
    label: 'Bulk Assign Deliveries',
    labelKey: 'navigation.bulkAssign',
    icon: 'pi pi-users',
    path: '/deliveries/assign',
    keywords: ['assign', 'dispatcher', 'allocation'],
    category: 'page',
  },
  {
    id: 'delivery-zones',
    label: 'Delivery Zones',
    labelKey: 'navigation.zones',
    icon: 'pi pi-map',
    path: '/delivery-zones',
    keywords: ['areas', 'regions', 'geography'],
    category: 'page',
  },
  {
    id: 'shippers',
    label: 'Shippers',
    labelKey: 'navigation.shippers',
    icon: 'pi pi-building',
    path: '/shippers',
    keywords: ['carriers', 'couriers', 'transport'],
    category: 'page',
  },
  {
    id: 'shipments',
    label: 'Shipments',
    labelKey: 'navigation.shipments',
    icon: 'pi pi-send',
    path: '/shipments',
    keywords: ['packages', 'tracking', 'parcels'],
    category: 'page',
  },

  // Pages - Finance
  {
    id: 'payments',
    label: 'Payments',
    labelKey: 'navigation.payments',
    icon: 'pi pi-wallet',
    path: '/payments',
    keywords: ['money', 'transactions', 'finance'],
    category: 'page',
    roles: ['admin', 'sales', 'accountant'],
  },
  {
    id: 'pending-payments',
    label: 'Pending Payments',
    labelKey: 'navigation.pendingPayments',
    icon: 'pi pi-clock',
    path: '/payments?status=awaiting_confirmation',
    keywords: ['awaiting', 'confirm', 'review'],
    category: 'page',
    roles: ['admin', 'sales', 'accountant'],
  },

  // Pages - Settings
  {
    id: 'settings',
    label: 'Settings',
    labelKey: 'navigation.settings',
    icon: 'pi pi-cog',
    path: '/settings',
    keywords: ['preferences', 'config', 'options'],
    category: 'setting',
    roles: ['admin', 'sales'],
  },

  // Actions
  {
    id: 'quick-order',
    label: 'Quick Order',
    labelKey: 'order.quickOrder',
    icon: 'pi pi-bolt',
    path: '/orders/quick',
    keywords: ['create', 'add', 'transaction', 'sale', 'vente', 'rapide'],
    category: 'action',
    roles: ['admin', 'sales'],
  },
  {
    id: 'new-customer',
    label: 'Add Customer',
    icon: 'pi pi-user-plus',
    path: '/customers/new',
    keywords: ['create', 'add', 'client'],
    category: 'action',
    roles: ['admin', 'sales'],
  },
  {
    id: 'new-order',
    label: 'New Order',
    icon: 'pi pi-plus',
    path: '/orders/new',
    keywords: ['create', 'add', 'purchase'],
    category: 'action',
    roles: ['admin', 'sales'],
  },
  {
    id: 'new-product',
    label: 'Add Product',
    icon: 'pi pi-plus',
    path: '/products/new',
    keywords: ['create', 'add', 'item'],
    category: 'action',
    roles: ['admin', 'sales'],
  },
  {
    id: 'new-arrival',
    label: 'Record Arrival',
    icon: 'pi pi-download',
    path: '/arrivals/new',
    keywords: ['create', 'add', 'consignment', 'incoming'],
    category: 'action',
    roles: ['admin', 'sales'],
  },
]

// Fuzzy search function
export function searchCommands(query: string): Command[] {
  if (!query.trim()) {
    return commands
  }

  const lowerQuery = query.toLowerCase()
  const terms = lowerQuery.split(/\s+/)

  return commands
    .map((cmd) => {
      const searchableText = [
        cmd.label.toLowerCase(),
        ...(cmd.keywords || []).map((k) => k.toLowerCase()),
      ].join(' ')

      // Calculate match score
      let score = 0
      for (const term of terms) {
        if (searchableText.includes(term)) {
          score += 1
          // Bonus for label match
          if (cmd.label.toLowerCase().includes(term)) {
            score += 2
          }
          // Bonus for exact start match
          if (cmd.label.toLowerCase().startsWith(term)) {
            score += 3
          }
        }
      }

      return { cmd, score }
    })
    .filter(({ score }) => score > 0)
    .sort((a, b) => b.score - a.score)
    .map(({ cmd }) => cmd)
}

// Get commands by category
export function getCommandsByCategory(category: Command['category']): Command[] {
  return commands.filter((cmd) => cmd.category === category)
}

// Filter commands by user role
export function filterCommandsByRole(commandList: Command[], role: UserRole | undefined): Command[] {
  if (!role) return []

  return commandList.filter((cmd) => {
    // If no roles specified, command is available to all
    if (!cmd.roles || cmd.roles.length === 0) return true
    // Otherwise, check if user's role is in the allowed roles
    return cmd.roles.includes(role)
  })
}

// Get all commands filtered by role
export function getCommandsForRole(role: UserRole | undefined): Command[] {
  return filterCommandsByRole(commands, role)
}
